import React, { useMemo } from "react";
import {
  Pressable,
  ScrollView,
  StyleSheet,
  TextStyle,
  View,
} from "react-native";
import { Icon, Text } from "react-native-paper";
import { Field, OrderByItem } from "../../config/companies/config";
import { getBoardValueStyleByRules } from "../../utils/boardValueStyle";
import { useAppSelector } from "../../redux/store";
import {
  calculateDisplayRows,
  shouldHideDisplayValue,
} from "../../utils/displayRows";
import FieldDisplay from "../FieldDisplay";
import { ValueDisplay } from "../ValueDisplay";
import { getNestedValue } from "../../utils/utils";

interface CollectionType {
  uniqIdNumber?: boolean;
  [key: string]: any;
}

interface DataItem {
  id: string | number;
  [key: string]: any;
}

interface TableTransposedLayoutProps {
  orderBy?: OrderByItem[];
  sortOptions: Record<string, "ascending" | "descending">;
  handleSort: (fieldName: string) => void;
  paginatedData: DataItem[];
  fields: Field[];
  collection: CollectionType;
  permittedFields: Field[];
  getRowStyle: (item: DataItem, fields: Field[]) => any;
  navigateToDetailsScreen: (
    fields: Field[],
    data: DataItem,
    collection: CollectionType,
  ) => void;
  t: (key: string) => string;
}

const isEmptyResponsiveValue = (
  field: Field,
  value: unknown,
  isInlineBooleanField: boolean,
): boolean => {
  if (isInlineBooleanField) {
    return false;
  }

  if (value === undefined || value === null) {
    return true;
  }

  if (typeof value === "string") {
    return value.trim().length === 0;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === "object") {
    if (field.type === "collectionPicker" && field.key) {
      return !getNestedValue(value, field.key);
    }

    return Object.keys(value).length === 0;
  }

  return false;
};

const renderResponsiveValue = (
  field: Field,
  value: unknown,
  valueStyle: TextStyle,
) => {
  if (
    field.type === "file" ||
    field.type === "fileAndDocument" ||
    field.type === "document"
  ) {
    return (
      <FieldDisplay
        field={field}
        value={value}
        valueStyle={valueStyle}
        showFileName={false}
      />
    );
  }

  return <ValueDisplay field={field} value={value} style={valueStyle} />;
};

const TableTransposedLayout: React.FC<TableTransposedLayoutProps> = ({
  orderBy,
  sortOptions,
  handleSort,
  paginatedData,
  fields,
  collection,
  permittedFields,
  getRowStyle,
  navigateToDetailsScreen,
  t,
}) => {
  const userRole = useAppSelector((state) => state.auth.user.role);
  const visibleFields = permittedFields;
  const sortableFields = useMemo(
    () =>
      visibleFields.filter((field) =>
        orderBy?.some((orderField) => orderField.field === field.name),
      ),
    [orderBy, visibleFields],
  );
  return (
    <>
      {sortableFields.length > 0 && (
        <ScrollView
          horizontal
          style={styles.sortScroll}
          contentContainerStyle={styles.sortScrollContent}
          showsHorizontalScrollIndicator={false}
        >
          {sortableFields.map((field) => {
            const direction = sortOptions[field.name];
            const directionIcon =
              direction === "descending" ? "chevron-down" : "chevron-up";

            return (
              <Pressable
                key={field.name}
                onPress={() => handleSort(field.name)}
                style={styles.sortChip}
              >
                <View style={styles.sortChipContent}>
                  <Text style={styles.sortChipText}>{t(field.label)}</Text>
                  <Icon source={directionIcon} size={16} color="#6B7280" />
                </View>
              </Pressable>
            );
          })}
        </ScrollView>
      )}

      <ScrollView
        style={styles.contentScroll}
        showsVerticalScrollIndicator={true}
        contentContainerStyle={styles.transposedContainer}
      >
        {paginatedData.map((item: DataItem) => {
          const rowStyle = getRowStyle(item, fields);

          return (
            <Pressable
              key={item.id}
              onPress={() => navigateToDetailsScreen(fields, item, collection)}
              style={[styles.transposedCard, rowStyle]}
            >
              {collection.uniqIdNumber && (
                <View style={styles.transposedRow}>
                  <Text style={styles.transposedLabel}>{t("ID")}</Text>
                  <View style={styles.transposedValueContainer}>
                    <Text style={styles.transposedValueText}>
                      {item.numberId ?? "-"}
                    </Text>
                  </View>
                </View>
              )}

              {calculateDisplayRows(
                visibleFields
                  .map((field) => {
                    const rawValue = item[field.name] ?? field.defaultValue;
                    const optionNormalizedValue =
                      Array.isArray(field.options) &&
                      rawValue !== null &&
                      typeof rawValue !== "undefined" &&
                      !field.options.includes(rawValue)
                        ? field.defaultValue
                        : rawValue;
                    const isInlineBooleanField =
                      field.type === "booleanCheckbox" &&
                      field.booleanDisplayMode === "inlineText";
                    const normalizedValue = isInlineBooleanField
                      ? Boolean(optionNormalizedValue)
                      : optionNormalizedValue;
                    const isEmpty = isEmptyResponsiveValue(
                      field,
                      normalizedValue,
                      isInlineBooleanField,
                    );
                    return {
                      field,
                      normalizedValue,
                      isInlineBooleanField,
                      isEmpty,
                      shouldHide:
                        shouldHideDisplayValue(
                          field,
                          normalizedValue,
                          userRole,
                        ) || isEmpty,
                      flex: field.displayRowFlex,
                    };
                  })
                  .filter((rowItem) => !rowItem.shouldHide),
              ).map((row, rowIndex) => {
                const isCompactRow =
                  row.length > 1 || row.some((rowItem) => rowItem.flex < 1);

                if (!isCompactRow) {
                  const { field, normalizedValue, isEmpty } = row[0];
                  const boardValueStyle = getBoardValueStyleByRules(
                    normalizedValue,
                    field.boardValueColorRules,
                  );
                  const mergedValueStyle = boardValueStyle
                    ? { ...styles.transposedValueText, ...boardValueStyle }
                    : styles.transposedValueText;
                  const shouldShowLabel =
                    field.hideLabel !== true &&
                    field.hideResponsiveLabel !== true;

                  return (
                    <View
                      key={`${item.id}-${field.name}`}
                      style={styles.transposedRow}
                    >
                      {shouldShowLabel && (
                        <Text style={styles.transposedLabel}>
                          {t(field.label)}
                        </Text>
                      )}
                      <View
                        style={[
                          styles.transposedValueContainer,
                          !shouldShowLabel &&
                            styles.transposedValueContainerFull,
                        ]}
                      >
                        {isEmpty ? (
                          <Text style={styles.emptyValue}>-</Text>
                        ) : (
                          renderResponsiveValue(
                            field,
                            normalizedValue,
                            mergedValueStyle,
                          )
                        )}
                      </View>
                    </View>
                  );
                }

                return (
                  <View
                    key={`${item.id}-${rowIndex}`}
                    style={styles.displayRow}
                  >
                    {row.map(
                      (
                        {
                          field,
                          normalizedValue,
                          isInlineBooleanField,
                          isEmpty,
                          flex,
                        },
                        index,
                      ) => {
                        const boardValueStyle = getBoardValueStyleByRules(
                          normalizedValue,
                          field.boardValueColorRules,
                        );
                        const mergedValueStyle = boardValueStyle
                          ? { ...styles.compactValueText, ...boardValueStyle }
                          : styles.compactValueText;
                        const shouldShowLabel =
                          field.hideLabel !== true &&
                          field.hideResponsiveLabel !== true;

                        return (
                          <View
                            key={`${item.id}-${field.name}`}
                            style={[
                              styles.displayRowPart,
                              row.length > 1
                                ? styles.displayRowPartCompact
                                : undefined,
                              { flex },
                              index === row.length - 1 && row.length > 1
                                ? styles.displayRowPartEnd
                                : undefined,
                            ]}
                          >
                            {shouldShowLabel && (
                              <Text style={styles.compactLabel}>
                                {t(field.label)}
                              </Text>
                            )}
                            {isEmpty ? (
                              <Text
                                style={[
                                  styles.emptyValue,
                                  styles.compactValueText,
                                ]}
                              >
                                -
                              </Text>
                            ) : (
                              renderResponsiveValue(
                                field,
                                normalizedValue,
                                mergedValueStyle,
                              )
                            )}
                          </View>
                        );
                      },
                    )}
                  </View>
                );
              })}
            </Pressable>
          );
        })}
      </ScrollView>
    </>
  );
};

const styles = StyleSheet.create({
  contentScroll: {
    flex: 1,
  },
  sortScroll: {
    maxHeight: 42,
    marginBottom: 8,
  },
  sortScrollContent: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 4,
    columnGap: 8,
  },
  sortChip: {
    minHeight: 34,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  sortChipContent: {
    flexDirection: "row",
    alignItems: "center",
    columnGap: 6,
  },
  sortChipText: {
    fontSize: 12,
    lineHeight: 14,
    textAlign: "center",
  },
  transposedContainer: {
    paddingBottom: 8,
    rowGap: 8,
  },
  transposedCard: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: "#FFFFFF",
  },
  transposedRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    columnGap: 8,
    paddingVertical: 4,
  },
  transposedLabel: {
    flex: 0.9,
    fontSize: 12,
    fontWeight: "600",
    color: "#374151",
  },
  transposedValueContainer: {
    flex: 1.1,
    alignItems: "flex-end",
  },
  transposedValueContainerFull: {
    flex: 1,
  },
  transposedValueText: {
    textAlign: "right",
  },
  displayRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    paddingVertical: 4,
    columnGap: 8,
  },
  displayRowPart: {
    minWidth: 0,
  },
  displayRowPartCompact: {
    alignItems: "flex-start",
  },
  displayRowPartEnd: {
    alignItems: "flex-end",
  },
  compactLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: "#374151",
    marginBottom: 4,
  },
  compactValueText: {
    textAlign: "left",
  },
  emptyValue: {
    color: "#9CA3AF",
    textAlign: "right",
  },
});

export default TableTransposedLayout;
